import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import { ICreateCampaignRequest, ILocationState, TemplateOrTemplateFilter } from '../../../../models';
import { useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { getDefaultDateStringValue, getDefaultTimeStringValue, isTemplateNew } from '../../../../models/UiUtils';
import { invalidateAllMyTemplatesFilter, invalidateMyTemplates } from '../../../../queries';
import { invalidateEmailAllCategoryTemplates } from '../../../../queries/EmailCampaign';
import { ITemplateCard, KnownCategories } from '../../../../viewmodels/AppViewModels';
import FinraImage from '../../../assets/finra.png';
import { Button } from '../../../components-v2/Button';
import { ButtonGroup } from '../../../components-v2/ButtonGroup';
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from '../../../components-v2/Dropdown';
import { bs } from '../../../styles/styles';
import { ChevronDown } from '../../svgs/icons/ChevronDown';
import { EmailIcon } from '../../svgs/icons/EmailIcon';
import { ExpiryIcon } from '../../svgs/icons/ExpiryIcon';
import { MessageSquareIcon } from '../../svgs/icons/MessageSquare';
import { OpenLetter } from '../../svgs/icons/OpenLetter';
import { SendNow } from '../../svgs/icons/SendNow';
import { hasCccKeyword } from '../../../containers/Campaigns/utils';
import { getTemplateApprovalStatus, hasTemplateReviewCapability } from '../../TemplateReviewStatusIndicator';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../Tooltip';
import { TemplateDateFields } from '../TemplateDateFields';
import { TemplateKeywords } from '../TemplateKeywords';
import { CampaignSummaryFromContent, CampaignSummaryFromContentShort, CampaignTemplateCardMenu } from './presentation';
import { styleSheet } from './styles';

export interface ICampaignTemplateCardProps {
	card: ITemplateCard;
	category?: KnownCategories;
	className?: string;
	industry?: Api.Industry;
	isAccountTemplate?: boolean;
	isFeatured?: boolean;
	isFinraReviewed?: boolean;
	onDelete?(): void;
	onShare?(isShared: boolean): void;
	onSocialClicked?(): void;
	rowIndex?: number;
	templateIndex?: number;
}

export const CampaignTemplateCard = ({
	card,
	category,
	industry,
	isAccountTemplate,
	isFeatured,
	isFinraReviewed,
	onDelete,
	onShare,
	onSocialClicked,
	templateIndex,
}: ICampaignTemplateCardProps) => {
	const userSession = useUserSession();
	const fullscreenModal = useFullscreenModal();
	const expirationDate = card?.schedule?.expirationDate ? moment(card?.schedule?.expirationDate) : undefined;
	const containsSocialMedia =
		userSession?.account?.features?.socialMedia?.enabled && card?.associatedTemplates?.length > 0;
	const showingSocialMedia = containsSocialMedia;
	const isMyTemplate = category === KnownCategories.MyTemplates;

	const onSocialMediaIndicatorClicked = () => {
		onSocialClicked?.();
	};

	const isNew = isTemplateNew(card);
	const handleInvalidateTemplates = () => {
		invalidateEmailAllCategoryTemplates();
		invalidateMyTemplates({ templateType: Api.TemplateType.Email });
		invalidateAllMyTemplatesFilter();
	};
	const canEditKeywords =
		card?.creator?.id === userSession.user.id || userSession.userRole.toLocaleLowerCase().includes('admin');
	const hasKeywords = card?.keywords && card.keywords.length > 0;
	const showKeywords = canEditKeywords || hasKeywords;
	const hasCcc = hasCccKeyword(card);
	const canReviewTemplates = hasTemplateReviewCapability(userSession);
	const approvalStatus = getTemplateApprovalStatus(card?.templateReview);

	const handlePreviewClick = async () => {
		const templates = new Api.TemplatesViewModel(userSession);
		const fetchedTemplate = await templates.getByIdExpandedByLastUsedBy(card.id);
		const locationState: ILocationState<any, ICreateCampaignRequest<TemplateOrTemplateFilter>> = {
			model: {
				context: { ...fetchedTemplate },
				type: 'Template',
			},
		};
		fullscreenModal?.history.push({
			pathname: '/email/campaigns/create/from-template',
			state: locationState,
		});
	};
	const handleReviewClick = async () => {
		const templates = new Api.TemplatesViewModel(userSession);
		const fetchedTemplate = await templates.getByIdExpandedByLastUsedBy(card.id);
		const locationState: ILocationState<any, ICreateCampaignRequest<TemplateOrTemplateFilter>> = {
			model: {
				context: { ...fetchedTemplate },
				type: 'Template',
			},
		};
		fullscreenModal?.history.push({
			pathname: '/email/campaigns/review',
			state: locationState,
		});
	};

	return (
		<div className={css(styleSheet.container, isFeatured && styleSheet.featured)}>
			<div className={css(styleSheet.topCorner)}>
				{expirationDate ? (
					<Tooltip>
						<TooltipTrigger className={css(styleSheet.expiryBadge)}>
							<ExpiryIcon fill={'currentColor'} />
							Time Sensitive
						</TooltipTrigger>
						<TooltipContent>
							{'This is a time-sensitive campaign that will stop sending on '}
							<time dateTime={getDefaultDateStringValue(expirationDate)}>
								{getDefaultDateStringValue(expirationDate)}
							</time>
							{' at '}
							<time dateTime={getDefaultTimeStringValue(expirationDate)}>
								{getDefaultTimeStringValue(expirationDate)}
							</time>
							.
						</TooltipContent>
					</Tooltip>
				) : null}
				{isNew ? <span className={css(styleSheet.newItem)}>New</span> : null}
			</div>
			{isFeatured && templateIndex === 0 && <SendNow className={css(styleSheet.sendNow)} />}
			{isAccountTemplate ? (
				<CampaignTemplateCardMenu card={card} userSession={userSession} onDelete={onDelete} onShare={onShare} />
			) : null}
			{isFeatured && templateIndex === 2 && <OpenLetter className={css(styleSheet.openLetter)} />}
			<div className={css(styleSheet.headerContainer)}>
				<div className={css(styleSheet.nameContainer, isFeatured ? styleSheet.flagHighlight : undefined)}>
					<h3 className={css(styleSheet.textContentTwoLines)} onClick={handlePreviewClick}>
						{card.name || 'Custom Post'}{' '}
					</h3>
					{showingSocialMedia ? (
						<Tooltip>
							<TooltipTrigger className={css(styleSheet.socialMediaTag)} onClick={onSocialMediaIndicatorClicked}>
								<MessageSquareIcon />
								<span>+</span>
								<EmailIcon fillColor='currentColor' />
							</TooltipTrigger>
							<TooltipContent>Also available for social media.</TooltipContent>
						</Tooltip>
					) : null}
				</div>
			</div>
			<div className={css(styleSheet.summaryContainer)}>
				{isAccountTemplate ? (
					<CampaignSummaryFromContent card={card} />
				) : (
					<CampaignSummaryFromContentShort card={card} />
				)}
			</div>
			<div className={css(styleSheet.buttonStack)}>
				<ButtonGroup styles={[bs.wFull]}>
					{!canReviewTemplates ? (
						<Button variant='secondary' size='sm' onClick={() => handlePreviewClick()} styles={[bs.flex1]}>
							Preview
						</Button>
					) : null}
					{canReviewTemplates ? (
						<>
							<Button variant='secondary' size='sm' onClick={handleReviewClick} styles={[bs.flex1]}>
								Review
							</Button>
							<Dropdown>
								<DropdownTrigger render={<Button variant='secondary' size='sm' />}>
									<ChevronDown />
								</DropdownTrigger>
								<DropdownContent align='end'>
									<DropdownItem onClick={() => handlePreviewClick()}>Preview</DropdownItem>
								</DropdownContent>
							</Dropdown>
						</>
					) : null}
				</ButtonGroup>
				{isFinraReviewed ? (
					<figure className={css(styleSheet.reviewedStack)}>
						<img src={FinraImage} height={14} />
						<span className={css(styleSheet.reviewed)}> Reviewed</span>
					</figure>
				) : null}
				{!isFinraReviewed ? <div className={css(styleSheet.approvalText)}>{approvalStatus}</div> : null}
			</div>
			{isMyTemplate && showKeywords ? (
				<TemplateKeywords
					templateId={card.id}
					industry={industry}
					keywords={card?.keywords}
					invalidateTemplates={handleInvalidateTemplates}
					readOnly={!canEditKeywords}
				/>
			) : null}
			{isMyTemplate && hasCcc ? (
				<TemplateDateFields
					template={card}
					templateType={Api.TemplateType.Email}
					onSuccess={handleInvalidateTemplates}
				/>
			) : null}
			{/* <Separator styles={[bs.mt2, bs.mb2]} /> */}
			{card.scope === Api.TemplateScope.Account && card.creator?.id !== userSession.user?.id ? (
				<div className={css(styleSheet.sharedByFooter)}>
					{card.creator?.firstName || card.creator?.lastName
						? `Shared by ${card.creator?.firstName} ${card.creator?.lastName}`
						: 'Shared'}
				</div>
			) : null}
		</div>
	);
};
